import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid, XCircleIcon as XSolid } from '@heroicons/react/24/solid';
import api from '../services/api';
import Spinner from '../components/common/Spinner';

const fetchAppointment = (token) =>
  api.get('/api/v1/appointments/confirm-token', { params: { token } }).then((r) => r.data.data);

const confirmAppt = (token) =>
  api.post('/api/v1/appointments/confirm-token/confirm', null, { params: { token } }).then((r) => r.data);

const cancelAppt = (token) =>
  api.post('/api/v1/appointments/confirm-token/cancel', null, { params: { token } }).then((r) => r.data);

export default function AppointmentConfirm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [finalAction, setFinalAction] = useState(null); // 'confirmed' | 'cancelled'

  const { data, isLoading, isError } = useQuery({
    queryKey: ['confirm-token', token],
    queryFn: () => fetchAppointment(token),
    enabled: Boolean(token),
    retry: false,
  });

  const confirmMutation = useMutation({
    mutationFn: () => confirmAppt(token),
    onSuccess: () => setFinalAction('confirmed'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelAppt(token),
    onSuccess: () => setFinalAction('cancelled'),
  });

  const isPending = confirmMutation.isPending || cancelMutation.isPending;

  // ── No token ──────────────────────────────────────────────────────────────
  if (!token) {
    return <ErrorCard message="No confirmation token provided. Please use the link from your reminder message." />;
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Shell>
        <div className="flex justify-center py-16"><Spinner /></div>
      </Shell>
    );
  }

  // ── Invalid / expired token ───────────────────────────────────────────────
  if (isError) {
    return (
      <Shell>
        <ErrorCard message="This confirmation link is invalid or has expired. Please contact your practice directly." />
      </Shell>
    );
  }

  // ── Already cancelled by practice ─────────────────────────────────────────
  if (data?.alreadyCancelled) {
    return (
      <Shell>
        <ResultCard
          icon={<XSolid className="h-12 w-12 text-danger-500" />}
          title="Appointment cancelled"
          message="This appointment has already been cancelled. Please contact your practice to reschedule."
          practice={null}
        />
      </Shell>
    );
  }

  const appt = data?.appointment;
  const previousAction = data?.actionTaken;

  // ── Already actioned (confirmed or cancelled earlier) ─────────────────────
  const action = finalAction || previousAction;
  if (action) {
    return (
      <Shell>
        {action === 'confirmed' ? (
          <ResultCard
            icon={<CheckSolid className="h-12 w-12 text-green-500" />}
            title="Appointment confirmed"
            message="Thank you! We look forward to seeing you."
            appt={appt}
            practice={appt}
          />
        ) : (
          <ResultCard
            icon={<XSolid className="h-12 w-12 text-danger-500" />}
            title="Appointment cancelled"
            message="Your appointment has been cancelled. Please contact us to reschedule."
            appt={appt}
            practice={appt}
          />
        )}
      </Shell>
    );
  }

  // ── Main confirmation UI ──────────────────────────────────────────────────
  return (
    <Shell>
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <CalendarDaysIcon className="h-10 w-10 text-primary-600 dark:text-primary-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Appointment reminder</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Please confirm or cancel your upcoming appointment.
          </p>
        </div>

        {/* Appointment details */}
        {appt && (
          <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 divide-y divide-neutral-200 dark:divide-neutral-700">
            <DetailRow icon={<CalendarDaysIcon className="h-4 w-4" />} label="Date & time">
              {format(parseISO(appt.startTime), "EEEE, MMMM d 'at' h:mm a")}
            </DetailRow>
            <DetailRow icon={<ClockIcon className="h-4 w-4" />} label="Type">
              {appt.typeName}
            </DetailRow>
            {appt.practitionerName && (
              <DetailRow icon={<UserIcon className="h-4 w-4" />} label="Provider">
                {appt.practitionerName}
              </DetailRow>
            )}
            {appt.practiceName && (
              <DetailRow icon={<BuildingOffice2Icon className="h-4 w-4" />} label="Practice">
                {appt.practiceName}
                {appt.practicePhone && (
                  <a href={`tel:${appt.practicePhone}`} className="ml-2 text-primary-600 dark:text-primary-400 underline text-xs">
                    {appt.practicePhone}
                  </a>
                )}
              </DetailRow>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => confirmMutation.mutate()}
            disabled={isPending}
            className="flex items-center justify-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold py-4 transition-colors disabled:opacity-60"
          >
            {confirmMutation.isPending
              ? <Spinner size="sm" />
              : <CheckCircleIcon className="h-5 w-5" />}
            Confirm
          </button>
          <button
            onClick={() => cancelMutation.mutate()}
            disabled={isPending}
            className="flex items-center justify-center gap-2 rounded-xl bg-danger-600 hover:bg-danger-700 text-white font-semibold py-4 transition-colors disabled:opacity-60"
          >
            {cancelMutation.isPending
              ? <Spinner size="sm" />
              : <XCircleIcon className="h-5 w-5" />}
            Cancel
          </button>
        </div>

        {(confirmMutation.isError || cancelMutation.isError) && (
          <p className="text-sm text-center text-danger-600 dark:text-danger-400">
            Something went wrong. Please try again or contact your practice.
          </p>
        )}

        <p className="text-xs text-center text-neutral-400 dark:text-neutral-500">
          Need to reschedule?{' '}
          {appt?.practicePhone
            ? <a href={`tel:${appt.practicePhone}`} className="underline">{appt.practicePhone}</a>
            : 'Contact your practice directly.'}
        </p>
      </div>
    </Shell>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex items-start justify-center pt-16 px-4 pb-20">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 tracking-wide uppercase">Sage</p>
        </div>
        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-neutral-200 dark:border-neutral-800 p-6">
          {children}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3 text-sm">
      <span className="text-neutral-400 dark:text-neutral-500 mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-neutral-900 dark:text-neutral-100 font-medium">{children}</p>
      </div>
    </div>
  );
}

function ResultCard({ icon, title, message, appt }) {
  return (
    <div className="text-center space-y-4">
      <div className="flex justify-center">{icon}</div>
      <div>
        <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{title}</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{message}</p>
      </div>
      {appt?.startTime && (
        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {format(parseISO(appt.startTime), "EEEE, MMMM d 'at' h:mm a")}
        </p>
      )}
      {appt?.practicePhone && (
        <a href={`tel:${appt.practicePhone}`} className="inline-block text-sm text-primary-600 dark:text-primary-400 underline">
          {appt.practicePhone}
        </a>
      )}
    </div>
  );
}

function ErrorCard({ message }) {
  return (
    <Shell>
      <div className="text-center space-y-3">
        <XSolid className="h-10 w-10 text-danger-400 mx-auto" />
        <p className="text-sm text-neutral-600 dark:text-neutral-400">{message}</p>
      </div>
    </Shell>
  );
}
